import { useContext, useMemo } from "react";
import { QueryClientContext } from "@tanstack/react-query";
import { api, type DocumentWithBlocks } from "@/lib/api";

export interface PreambleAccess {
  /** The document's custom preamble as the editor last had it. */
  read: () => string;
  /** Save a new one (the same call Document settings makes) and keep the editor's copy in step. */
  write: (next: string) => Promise<void>;
}

/**
 * The document's custom preamble, as Document settings edits it: read from the
 * editor's document query, written with PUT /api/documents/{id}. Templates add
 * the library line a figure needs here. Without a query client (a bare test
 * mount) it reads as empty and still writes.
 */
export function useDocumentPreamble(documentId: string | null | undefined): PreambleAccess {
  const client = useContext(QueryClientContext);
  return useMemo(() => {
    const key = ["document", documentId];
    return {
      read: () => (documentId ? client?.getQueryData<DocumentWithBlocks>(key)?.customPreamble ?? "" : ""),
      write: async (next: string) => {
        if (!documentId) return;
        await api.documents.update(documentId, { customPreamble: next });
        client?.setQueryData<DocumentWithBlocks>(key, (d) => (d ? { ...d, customPreamble: next } : d));
      },
    };
  }, [client, documentId]);
}
